import React, { useState, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { list, uploadData } from "aws-amplify/storage";
import { setModalOpen, setModalState, modalViewsConst } from "../../store/modalSlice";
import ModalBase from "./ModalBase";
import { ReactComponent as LayersIcon } from "../../assets/icons/layers-icon.svg";
import { Skeleton, SkeletonText, SkeletonGroup } from "../Skeleton";

interface S3FileItem {
  path: string;
  fileName: string;
  lastModified?: Date;
  size?: number;
}

interface ProcessedModelFilesModalProps {
  onClose?: () => void;
  model?: {
    id: string;
    label: string;
    routeLong: string;
    routeShort: string;
    organizationId?: string;
  };
}

const ProcessedModelFilesModal: React.FC<ProcessedModelFilesModalProps> = ({
  onClose,
  model,
}) => {
  const dispatch = useDispatch();
  const user = useSelector((state: any) => state.user);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const uploadTaskRef = useRef<any>(null);
  const [files, setFiles] = useState<S3FileItem[]>([]);
  const [loadingFiles, setLoadingFiles] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [dragging, setDragging] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);

  const isAdmin = user?.details?.groups?.includes("Admin");
  const isAdminReadOnly = user?.details?.groups?.includes("AdminReadOnly");

  const handleClose = () => {
    if (onClose) {
      onClose();
    } else {
      dispatch(setModalOpen(false));
    }
  };

  useEffect(() => {
    if ((isAdmin || isAdminReadOnly) && model?.routeLong) {
      loadFiles();
    }
  }, [isAdmin, isAdminReadOnly, model?.routeLong]);

  useEffect(() => {
    return () => {
      uploadTaskRef.current?.cancel();
    };
  }, []);

  const loadFiles = async () => {
    setLoadingFiles(true);
    setError(null);
    try {
      const result = await list({ path: model!.routeLong, options: { listAll: true } });
      const items: S3FileItem[] = result.items
        .filter((item) => {
          const parts = item.path.split("/");
          const name = parts[parts.length - 1];
          // exclude placeholder files (e.g. 2024-08-20-06-placeholder.json) from here — they appear in the download modal
          return name.endsWith(".json") && name.length > 0 && !name.includes("placeholder");
        })
        .map((item) => {
          const parts = item.path.split("/");
          return {
            path: item.path,
            fileName: parts[parts.length - 1],
            lastModified: item.lastModified,
            size: item.size,
          };
        })
        .sort((a, b) => {
          if (!a.lastModified || !b.lastModified) return 0;
          return b.lastModified.getTime() - a.lastModified.getTime();
        });
      setFiles(items);
    } catch (err) {
      console.error("Error listing S3 files:", err);
      setError("Failed to load files. Please try again.");
    } finally {
      setLoadingFiles(false);
    }
  };

  const uploadFile = async (file: File) => {
    if (!file.name.endsWith(".json")) {
      setUploadError("Only .json files are allowed.");
      return;
    }
    setUploading(true);
    setUploadProgress(0);
    setUploadError(null);
    try {
      const basePath = model!.routeLong.endsWith("/") ? model!.routeLong : `${model!.routeLong}/`;
      const task = uploadData({
        path: `${basePath}${file.name}`,
        data: file,
        options: {
          contentType: "application/json",
          onProgress: ({ transferredBytes, totalBytes }) => {
            if (totalBytes) {
              setUploadProgress(Math.round((transferredBytes / totalBytes) * 100));
            }
          },
        },
      });
      uploadTaskRef.current = task;
      await task.result;
      uploadTaskRef.current = null;
      await loadFiles();
    } catch (err) {
      console.error("Error uploading file:", err);
      setUploadError("Upload failed. Please try again.");
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) uploadFile(file);
    e.target.value = "";
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) uploadFile(file);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragging(true);
  };

  const handleDragLeave = () => setDragging(false);

  const handleDeleteClick = (file: S3FileItem) => {
    dispatch(
      setModalState({
        modalOpen: true,
        modalView: modalViewsConst.confirmDeleteFile,
        properties: {
          __modalSize: "sm",
          filePath: file.path,
          fileName: file.fileName,
          model,
        },
      })
    );
  };

  const formatSize = (bytes?: number) => {
    if (bytes === undefined) return "-";
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const formatDate = (date?: Date) => {
    if (!date) return "-";
    return date.toLocaleString();
  };

  if (!user) {
    return (
      <ModalBase title="Manage Model Files" icon={<LayersIcon className="h-6 w-6 text-[#A2BBBF]" />} onClose={handleClose} size="lg">
        <div className="flex flex-col gap-3 py-6">
          <SkeletonText lines={3} />
        </div>
      </ModalBase>
    );
  }

  if (!isAdmin && !isAdminReadOnly) {
    return (
      <ModalBase title="Manage Model Files" onClose={handleClose} size="sm">
        <div className="flex flex-col items-center gap-3 py-8 text-center">
          <p className="text-gray-700 font-medium">Admin access required</p>
          <p className="text-sm text-gray-500">
            You must be an Admin to manage processed model files.
          </p>
        </div>
      </ModalBase>
    );
  }

  return (
    <ModalBase
      title="Manage Model Files"
      icon={<LayersIcon className="h-6 w-6 text-[#A2BBBF]" />}
      onClose={handleClose}
      size="lg"
      footer={
        <button className="eai-btn" onClick={handleClose}>
          Close
        </button>
      }
    >
      <div className="flex flex-col gap-6">
        {/* Model info */}
        <div className="flex flex-col gap-1">
          <p className="text-sm text-gray-500">Model</p>
          <p className="font-medium text-gray-900">{model?.label}</p>
          <p className="text-xs text-gray-400 font-mono break-all">{model?.routeLong}</p>
        </div>

        {/* Upload area — Admin only */}
        {isAdmin && <div className="flex flex-col gap-2">
          <p className="text-sm font-medium text-gray-700">Upload JSON file</p>
          <div
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onClick={() => !uploading && fileInputRef.current?.click()}
            className={`border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors ${
              dragging
                ? "border-eai-primary-accent bg-blue-50"
                : "border-gray-300 hover:border-gray-400 bg-gray-50"
            } ${uploading ? "cursor-not-allowed opacity-60" : ""}`}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".json"
              onChange={handleFileInputChange}
              className="hidden"
              disabled={uploading}
            />
            {uploading ? (
              <div className="flex flex-col items-center gap-3">
                <div className="w-full bg-gray-200 rounded-full h-2">
                  <div
                    className="bg-eai-primary-accent h-2 rounded-full transition-all"
                    style={{ width: `${uploadProgress}%` }}
                  />
                </div>
                <p className="text-sm text-gray-600">Uploading… {uploadProgress}%</p>
              </div>
            ) : (
              <>
                <p className="text-sm text-gray-600">
                  <span className="font-medium text-eai-primary-accent">Click to upload</span>
                  {" "}or drag and drop
                </p>
                <p className="text-xs text-gray-400 mt-1">.json files only</p>
              </>
            )}
          </div>
          {uploadError && (
            <p className="text-sm text-red-600">{uploadError}</p>
          )}
        </div>}

        {/* File list */}
        <div className="flex flex-col gap-2">
          <p className="text-sm font-medium text-gray-700">
            Existing files
            {!loadingFiles && (
              <span className="ml-2 text-gray-400 font-normal">({files.length})</span>
            )}
          </p>

          {error && (
            <p className="text-sm text-red-600">{error}</p>
          )}

          {/* One row per file the list is about to show */}
          {loadingFiles ? (
            <SkeletonGroup count={3} label="Loading files" className="flex flex-col gap-2 py-2">
              <div className="flex items-center gap-3 py-2">
                <Skeleton className="h-8 w-8 shrink-0" radius="md" />
                <div className="min-w-0 flex-1">
                  <Skeleton className="h-3.5 w-1/2" />
                  <Skeleton className="mt-1.5 h-3 w-1/3" />
                </div>
                <Skeleton className="h-eai-control-icon w-eai-control-icon shrink-0" radius="full" />
              </div>
            </SkeletonGroup>
          ) : files.length === 0 ? (
            <p className="text-sm text-gray-500 py-4 text-center">
              No files uploaded yet.
            </p>
          ) : (
            <div className="border border-gray-200 rounded-lg overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-gray-200">
                  <tr>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      File name
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Size
                    </th>
                    <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Last modified
                    </th>
                    <th className="px-4 py-2" />
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 bg-white">
                  {files.map((file) => (
                    <tr key={file.path} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-mono text-xs text-gray-800 max-w-[260px] truncate">
                        {file.fileName}
                      </td>
                      <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">
                        {formatSize(file.size)}
                      </td>
                      <td className="px-4 py-3 text-gray-500 text-xs whitespace-nowrap">
                        {formatDate(file.lastModified)}
                      </td>
                      <td className="px-4 py-3 text-right">
                        {isAdmin && (
                          <button
                            onClick={() => handleDeleteClick(file)}
                            className="text-red-500 hover:text-red-700 text-xs font-medium"
                          >
                            Delete
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </ModalBase>
  );
};

export default ProcessedModelFilesModal;
